<script>
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import BoardResource from '@/resources/board'
import ReservationResource from '@/resources/reservation'
import LocationResource from '@/resources/location'
import moment from 'moment'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'MenuScreen',
    components: {},
    computed: {
        ...mapGetters(['locationSelected']),
        locationId() {
            return this.$route.query.location || this.resolvedLocationId || null
        },
        // The location named in the URL (/menu/<suffix> or ?location=), falling
        // back to the geographically-nearest one only when the URL has none.
        activeLocation() {
            return this.location || this.locationSelected
        },
        activeBranding() {
            return (this.activeLocation && this.activeLocation.attributes && this.activeLocation.attributes.branding) || {}
        },
        menuLogoUrl() {
            const branding = this.activeBranding
            return (branding.readonly_values && branding.readonly_values.design_logo_image_url) || branding.design_logo_image_url || this.$branding.storeVal('design_logo_image_url', 'https://drivesims-assets-public.s3.us-west-1.amazonaws.com/images/shiftarcade-logo-web.png')
        },
        priceRows() {
            const seen = {}
            const rows = []
            for (const resource of this.calendarResources) {
                const attrs = resource.attributes
                if (seen[attrs.type]) {
                    continue
                }
                seen[attrs.type] = true
                rows.push({
                    name: attrs.type_name || attrs.type,
                    costCents: attrs.cost_cents,
                    costUnit: attrs.cost_unit,
                })
            }
            return rows
        },
        seatRows() {
            return this.calendarResources
                .filter((calendar) => calendar.attributes.active !== false)
                .map((calendar) => {
                    const seated = this.seatedMap[calendar.id] || {}
                    const current = seated.current || null
                    const next = seated.next || null
                    const nextHolder = this.reservationHolder(next)
                    const sameHolder = !!nextHolder && nextHolder === this.reservationHolder(current)
                    return {
                        id: calendar.id,
                        name: calendar.attributes.name,
                        type: calendar.attributes.type_name || '',
                        current: current ? new ReservationResource(current) : null,
                        next: nextHolder && !sameHolder ? new ReservationResource(next) : null,
                        freeInMinutes: current && !nextHolder ? this.minutesToNextSession(calendar) : null,
                        through: sameHolder ? moment(next.time).add((calendar.attributes.reservation_block_length || 1) * 15, 'minutes').format('h:mm a') : null,
                    }
                })
        },
        boardModules() {
            return this.boardResources
                .filter((board) => (this.boardResultsMap[board.id] || []).length > 0)
                .map((board) => ({
                    type: 'board',
                    key: `board-${board.id}`,
                    board,
                    rows: this.boardResultsMap[board.id].slice(0, 10),
                }))
        },
        modules() {
            const list = []

            list.push({ type: 'welcome', key: 'welcome' })

            if (this.priceRows.length) {
                list.push({ type: 'pricing', key: 'pricing' })
            }

            if (this.locationId && this.seatRows.length) {
                list.push({ type: 'seats', key: 'seats' })
            }

            if (this.upcomingReservations.length) {
                list.push({ type: 'upcoming', key: 'upcoming' })
            }

            for (const boardModule of this.boardModules) {
                list.push(boardModule)
            }

            return list
        },
        activeModule() {
            return this.modules[this.activeIndex % Math.max(this.modules.length, 1)] || { type: 'welcome', key: 'welcome' }
        },
        boardTop() {
            return (this.activeModule.rows || []).slice(0, 3)
        },
        boardRest() {
            return (this.activeModule.rows || []).slice(3)
        },
        clockLabel() {
            return this.now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        },
        dateLabel() {
            return this.now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
        },
    },
    data() {
        return {
            loading: true,
            reloadPageEveryMs: 6e5,
            rotateMs: 12000,
            boardRotateMs: 22000,
            carRotateMs: 18000,
            rotateId: null,
            clockId: null,
            carId: null,
            activeIndex: 0,
            carIndex: 0,
            carBackgrounds: [require('@/assets/porsche-gt3.jpg'), require('@/assets/porsche-nurburgring.jpg'), require('@/assets/porsche.jpg')],
            now: new Date(),
            location: null,
            resolvedLocationId: null,
            calendarResources: [],
            boardResources: [],
            boardResultsMap: {},
            upcomingReservations: [],
            seatedMap: {},
            seatsId: null,
            seatsRefreshMs: 60000,
        }
    },
    methods: {
        timeDateFormattedPretty,
        money(cents) {
            return `${this.currencySymbol()}${this.moneyFormat(cents)}`
        },
        rankLabel(item) {
            return item.attributes.name || item.attributes.display_name
        },
        rankValue(board, item) {
            return board.attributes.calculation === 'POINTS' ? `${item.attributes.points} pts` : item.attributes.hms
        },
        isTimedBoard(board) {
            return board.attributes.calculation !== 'POINTS'
        },
        carName(item) {
            return item.attributes.car_name || item.attributes.car || ''
        },
        reservationName(reservation) {
            const readonly = reservation.attributes.readonly_values || {}
            if (reservation.attributes.is_blackout) {
                return 'Reserved'
            }
            return readonly.seated_user_display_name || readonly.user_display_name || 'Reserved'
        },
        reservationHolder(reservation) {
            if (!reservation) {
                return null
            }
            if (reservation.is_blackout) {
                return 'blackout'
            }
            const readonly = reservation.readonly_values || {}
            const holder = readonly.seated_user || reservation.user
            return holder ? String(holder) : null
        },
        minutesUntil(dateTime) {
            return Math.max(1, Math.ceil((dateTime - this.now) / 60000))
        },
        minutesToNextSession(calendar) {
            const length = calendar.attributes.reservation_block_length || 1
            const minutesNow = this.now.getHours() * 60 + this.now.getMinutes() + this.now.getSeconds() / 60
            const block = Math.floor(minutesNow / 15) + length
            return Math.max(1, Math.ceil((block - (block % length)) * 15 - minutesNow))
        },
        reservationSeat(reservation) {
            const readonly = reservation.attributes.readonly_values || {}
            return readonly.calendar_name || readonly.calendar || ''
        },
        async loadBoards() {
            if (this.locationId) {
                const [locationBoards, globalBoards] = await Promise.all([BoardResource.filterAll({ location: this.locationId, show_on_menu: true }).catch(() => []), BoardResource.filterAll({ location__isnull: true, show_on_menu: true }).catch(() => [])])
                this.boardResources = locationBoards.concat(globalBoards)
            } else {
                this.boardResources = await BoardResource.filterAll({ location__isnull: true, show_on_menu: true }).catch(() => [])
            }

            await Promise.all(
                this.boardResources.map(async (board) => {
                    try {
                        const result = await board.getResults({ count: 10, page_size: 10 })
                        this.$set(this.boardResultsMap, board.id, result.resources)
                    } catch (e) {
                        this.$set(this.boardResultsMap, board.id, [])
                    }
                })
            )
        },
        async loadSeats() {
            if (!this.locationId) {
                return
            }
            await Promise.all(
                this.calendarResources.map(async (calendar) => {
                    try {
                        this.$set(this.seatedMap, calendar.id, (await calendar.seated()) || {})
                    } catch (e) {
                        this.$set(this.seatedMap, calendar.id, {})
                    }
                })
            )
        },
        async loadUpcoming() {
            try {
                const filters = { ordering: 'time' }
                if (this.locationId) {
                    filters.location = this.locationId
                }
                const page = await ReservationResource.filter(filters)
                const cutoff = new Date()
                this.upcomingReservations = (page.resources || [])
                    .filter((reservation) => {
                        try {
                            return reservation.dateTime > cutoff
                        } catch (e) {
                            return false
                        }
                    })
                    .slice(0, 6)
            } catch (e) {
                this.upcomingReservations = []
            }
        },
        rotate() {
            if (this.modules.length > 1) {
                this.activeIndex = (this.activeIndex + 1) % this.modules.length
            }
        },
        goTo(index) {
            clearTimeout(this.rotateId)
            this.activeIndex = index
            this.scheduleRotate()
        },
        scheduleRotate() {
            const dwell = this.activeModule.type === 'board' ? this.boardRotateMs : this.rotateMs
            this.rotateId = setTimeout(() => {
                this.rotate()
                this.scheduleRotate()
            }, dwell)
        },
        applyLocationBranding() {
            // Drive the display off the URL-named location's own brand, not the
            // geographically-nearest one that App.vue applied on first load.
            const branding = (this.activeLocation && this.activeLocation.attributes && this.activeLocation.attributes.branding) || null
            if (!branding || !this.$branding) {
                return
            }
            this.$branding.setTheme({
                theme: 'DARK',
                primary: branding.design_color_primary,
                secondary: branding.design_color_secondary,
                fontFamily: branding.design_font_family,
                fontURL: branding.design_font_url,
                extraCSS: branding.design_extra_css,
            })
        },
        async resolveLocationBySuffix() {
            // Store Menu links are /menu/<last 6 of location.id> instead of the
            // full UUID. Resolve it to the real id before anything loads. A plain
            // /menu (or an explicit ?location=) skips this and shows the global menu.
            const suffix = this.$route.params.locationSuffix
            if (!suffix || this.$route.query.location) {
                return
            }
            try {
                const locations = await LocationResource.all()
                const match = (locations || []).find((location) => String(location.id).endsWith(String(suffix)))
                if (match) {
                    this.resolvedLocationId = match.id
                }
            } catch (e) {
                // Fall back to the global menu.
            }
        },
    },
    async mounted() {
        document.documentElement.setAttribute('data-theme', 'dark')

        await this.resolveLocationBySuffix()

        const calendarFilters = this.locationId ? { location: this.locationId } : {}

        const [calendars, location] = await Promise.all([CalendarResource.filterAll(calendarFilters).catch(() => []), this.locationId ? LocationResource.detail(this.locationId).catch(() => null) : Promise.resolve(null)])

        this.calendarResources = calendars
        this.location = location

        this.applyLocationBranding()

        await Promise.all([this.loadBoards(), this.loadUpcoming(), this.loadSeats()])

        // Let the branded fonts settle so the first paint is not a swap.
        await Promise.race([document.fonts ? document.fonts.ready : Promise.resolve(), new Promise((resolve) => setTimeout(resolve, 2500))])
        await this.$nextTick()

        this.loading = false
        this.scheduleRotate()
        this.clockId = setInterval(() => (this.now = new Date()), 15000)
        this.seatsId = setInterval(() => this.loadSeats(), this.seatsRefreshMs)
        this.carId = setInterval(() => (this.carIndex = (this.carIndex + 1) % this.carBackgrounds.length), this.carRotateMs)
        setTimeout(() => window.location.reload(), this.reloadPageEveryMs)
    },
    beforeDestroy() {
        clearTimeout(this.rotateId)
        clearInterval(this.clockId)
        clearInterval(this.carId)
        clearInterval(this.seatsId)
    },
}
</script>

<template>
    <div class="menu" v-if="!loading">
        <transition name="menu-carfade">
            <div class="menu__car" :key="carIndex" :style="{ backgroundImage: `url(${carBackgrounds[carIndex]})` }"></div>
        </transition>
        <div class="menu__livery"></div>

        <header class="menu__top">
            <div class="menu__brand">
                <b-img class="menu__logo" :src="menuLogoUrl" />
            </div>
            <div class="menu__clock">
                <div class="menu__time">{{ clockLabel }}</div>
                <div class="menu__date">{{ dateLabel }}</div>
            </div>
        </header>

        <main class="menu__stage">
            <transition name="menu-fade" mode="out-in">
                <section class="menu__module" :key="activeModule.key">
                    <div class="menu__panel" v-if="activeModule.type === 'welcome'">
                        <div class="menu__welcome">
                            <div class="menu__kicker">Willkommen bei</div>
                            <h1 class="menu__headline">{{ activeLocation && activeLocation.attributes.company_name }}</h1>
                            <p class="menu__lead">Echte Fahrzeuge. Echte Strecken. Echte Zeiten. Buche einen Simulator und fahre eine Runde für die Bestenliste.</p>
                            <p class="menu__apex" v-if="$appConfig('APEX_CLUB_ENABLED', true)">
                                Mitglieder sparen 20 Prozent mit dem <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong>
                            </p>
                        </div>
                    </div>

                    <div class="menu__panel" v-else-if="activeModule.type === 'pricing'">
                        <div class="menu__moduletitle">Simulatorpreise</div>
                        <div class="menu__prices">
                            <div class="menu__price" v-for="(row, i) in priceRows" :key="i">
                                <span class="menu__pricename">{{ row.name }}</span>
                                <span class="menu__pricecost"
                                    ><strong>{{ money(row.costCents) }}</strong> <span class="menu__priceunit">{{ row.costUnit }}</span></span
                                >
                            </div>
                        </div>
                    </div>

                    <div class="menu__panel" v-else-if="activeModule.type === 'seats'">
                        <div class="menu__moduletitle">Simulatoren</div>
                        <div class="menu__seats">
                            <div class="menu__seat" v-for="seat in seatRows" :key="seat.id">
                                <div class="menu__seatwho">
                                    <span class="menu__seatname">{{ seat.name }}</span>
                                    <span class="menu__seatnext">{{ seat.type || '\u00a0' }}</span>
                                </div>
                                <div class="menu__seatwho">
                                    <span :class="{ 'menu__seatopen': !seat.current }">{{ seat.current ? reservationName(seat.current) : 'Frei' }}</span>
                                    <span class="menu__seatnext" v-if="seat.freeInMinutes"><span class="menu__seatopen">Frei in {{ seat.freeInMinutes }} Min.</span></span>
                                    <span class="menu__seatnext" v-else-if="seat.next">{{ reservationName(seat.next) }} in {{ minutesUntil(seat.next.dateTime) }} Min.</span>
                                    <span class="menu__seatnext" v-else-if="seat.through">{{ seat.current.attributes.is_blackout ? "Reserviert bis" : "Gebucht bis mindestens" }} {{ seat.through }}</span>
                                    <span class="menu__seatnext" v-else>&nbsp;</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="menu__panel" v-else-if="activeModule.type === 'upcoming'">
                        <div class="menu__moduletitle">Als Nächstes</div>
                        <div class="menu__upcoming">
                            <div class="menu__reservation" v-for="(reservation, i) in upcomingReservations" :key="i">
                                <span class="menu__restime">{{ reservation.getTimeFormatted() }}</span>
                                <span class="menu__resname">{{ reservationName(reservation) }}</span>
                                <span class="menu__resseat">{{ reservationSeat(reservation) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="menu__panel menu__panel--board" v-else-if="activeModule.type === 'board'">
                        <div class="menu__moduletitle">
                            {{ activeModule.board }}
                            <span class="menu__modulesub" v-if="activeModule.board.attributes.time_frame_pretty">{{ activeModule.board.attributes.time_frame_pretty }}</span>
                        </div>
                        <div class="menu__leaderboard">
                            <div class="menu__lbtop">
                                <div class="menu__lbrow" v-for="(item, i) in boardTop" :key="`top-${i}`" :class="{ 'menu__lbrow--lead': i === 0 }">
                                    <span class="menu__lbpos">{{ item.attributes.rank || i + 1 }}</span>
                                    <img v-if="item.attributes.flag_url" :src="item.attributes.flag_url" class="menu__lbflag" />
                                    <div class="menu__lbracer">
                                        <span class="menu__lbname">{{ rankLabel(item) }}</span>
                                        <span class="menu__lbcar" v-if="isTimedBoard(activeModule.board) && carName(item)">{{ carName(item) }}</span>
                                    </div>
                                    <span class="menu__lbtime">{{ rankValue(activeModule.board, item) }}</span>
                                </div>
                            </div>
                            <div class="menu__lbmore" v-if="boardRest.length">
                                <div class="menu__lbtrack" :class="{ 'is-scrolling': boardRest.length > 3 }" :style="{ '--menu-dwell': boardRotateMs + 'ms' }">
                                    <div class="menu__lbrow" v-for="(item, i) in boardRest" :key="`rest-${i}`">
                                        <span class="menu__lbpos">{{ item.attributes.rank || i + 4 }}</span>
                                        <img v-if="item.attributes.flag_url" :src="item.attributes.flag_url" class="menu__lbflag" />
                                        <div class="menu__lbracer">
                                            <span class="menu__lbname">{{ rankLabel(item) }}</span>
                                            <span class="menu__lbcar" v-if="isTimedBoard(activeModule.board) && carName(item)">{{ carName(item) }}</span>
                                        </div>
                                        <span class="menu__lbtime">{{ rankValue(activeModule.board, item) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </transition>
        </main>

        <!-- <footer class="menu__bottom">
            <div class="menu__cta">
                <b-img :src="require('@/assets/qr-link.svg')" class="menu__qr" />
                <span>Scan to check availability and book</span>
            </div>
        </footer> -->

        <div class="menu__dots">
            <span v-for="(mod, i) in modules" :key="mod.key" class="menu__dot" :class="{ 'is-on': i === activeIndex % modules.length }" role="button" @click="goTo(i)"></span>
        </div>
    </div>

    <div class="menu menu--loading" v-else>
        <div class="menu__loader">
            <span class="menu__loaderkick"></span>
            <div class="menu__loadertext">Menü wird vorbereitet</div>
        </div>
    </div>
</template>

<style lang="scss">
.menu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: radial-gradient(120% 90% at 90% -20%, rgba(255, 255, 255, 0.06), transparent 55%), radial-gradient(90% 70% at -12% 118%, var(--sos-accent-soft), transparent 55%), radial-gradient(78% 60% at 112% 120%, var(--sos-teal-soft), transparent 55%),
        linear-gradient(180deg, var(--sos-display-bg-2), var(--sos-display-bg));
    color: var(--sos-display-ink);
    font-family: var(--sos-font);
    padding: 3vh 4vw;
}

// Rotating GT3 car, sitting just under the dark ground so it reads as texture.
.menu__car {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    opacity: 0.12;
    filter: grayscale(0.25) contrast(1.05);
    pointer-events: none;
    z-index: 0;
}

.menu__livery {
    display: none;
}

.menu__top,
.menu__stage,
.menu__bottom {
    position: relative;
    z-index: 2;
}

.menu__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: none;
}
.menu__brand {
    flex: 0 1 auto;
    min-width: 0;
}
.menu__logo {
    display: block;
    width: auto;
    height: auto;
    max-width: 20vw;
    max-height: 10vh;
    object-fit: contain;
}
.menu__clock {
    text-align: right;
}
.menu__time {
    font-family: var(--sos-font-head);
    font-weight: 700;
    font-size: 2.6vw;
    line-height: 1;
    font-variant-numeric: tabular-nums;
}
.menu__date {
    font-family: var(--sos-font-head);
    font-weight: 600;
    font-size: 1vw;
    color: var(--sos-display-ink-dim);
    margin-top: 0.3vw;
}

.menu__stage {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    overflow: hidden;
    padding: 1.5vh 0;
}
.menu__module {
    width: 100%;
    max-width: 78vw;
    max-height: 75vh;
    display: flex;
    flex-direction: column;
}
.menu__panel {
    text-align: center;
}
.menu__panel--board {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.menu__moduletitle {
    flex: none;
    font-family: var(--sos-font-head);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 3vw;
    line-height: 1;
    margin-bottom: 3vh;
}
.menu__moduletitle::after {
    content: '';
    display: block;
    width: 7vw;
    height: 0.9vw;
    margin: 1.5vh auto 0;
    background: linear-gradient(90deg, var(--sos-brand) 0 62%, transparent 62% 72%, var(--sos-teal) 72% 100%);
    transform: skewX(-18deg);
}
.menu__modulesub {
    display: block;
    font-size: 1.1vw;
    font-weight: 600;
    color: var(--sos-display-ink-dim);
    margin-top: 0.4vw;
}

.menu__kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.8vw;
    font-family: var(--sos-font-head);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.34em;
    font-size: 1.15vw;
    color: var(--sos-display-ink-dim);
}
.menu__kicker::before {
    content: '';
    width: 3.2vw;
    height: 0.9vw;
    background: linear-gradient(90deg, var(--sos-brand) 0 62%, transparent 62% 72%, var(--sos-teal) 72% 100%);
    transform: skewX(-18deg);
}

.menu__headline {
    font-family: var(--sos-font-head);
    font-weight: 700;
    text-transform: uppercase;
    font-size: 5vw;
    line-height: 1;
    margin: 0.8vw 0 0;
}
.menu__lead {
    font-size: 1.6vw;
    color: var(--sos-display-ink-dim);
    max-width: 46vw;
    margin: 1.5vh auto 0;
}
.menu__apex {
    font-size: 1.4vw;
    margin-top: 2vh;

    strong {
        color: var(--sos-accent);
    }
}

.menu__prices {
    max-width: 52vw;
    margin: 0 auto;
}
.menu__price {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 1.4vh 0;
    border-bottom: 1px solid var(--sos-display-line);
    font-size: 2vw;
}
.menu__pricename {
    font-family: var(--sos-font-head);
    font-weight: 600;
}
.menu__pricecost {
    font-variant-numeric: tabular-nums;

    strong {
        font-family: var(--sos-font-head);
    }
}
.menu__priceunit {
    font-size: 1.2vw;
    color: var(--sos-display-ink-dim);
}

.menu__upcoming {
    max-width: 56vw;
    margin: 0 auto;
}
.menu__reservation {
    display: flex;
    align-items: center;
    gap: 2vw;
    padding: 1.3vh 0;
    border-bottom: 1px solid var(--sos-display-line);
    font-size: 1.8vw;
}
.menu__restime {
    font-family: var(--sos-font-mono);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-width: 8vw;
}
.menu__resname {
    flex: 1;
    text-align: left;
    font-family: var(--sos-font-head);
    font-weight: 600;
}
.menu__seats {
    display: grid;
    grid-template-columns: repeat(2, max-content);
    justify-content: center;
    column-gap: 5vw;
}
.menu__seat {
    display: grid;
    grid-template-columns: 13vw 18vw;
    column-gap: 1.5vw;
    align-items: start;
    padding: 1.2vh 0;
    border-bottom: 1px solid var(--sos-display-line);
    font-size: 1.5vw;
    text-align: left;
}
.menu__seatname {
    font-family: var(--sos-font-head);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.menu__seatwho {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-family: var(--sos-font-head);
    font-weight: 600;
}
.menu__seatwho > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.menu__seatopen {
    color: var(--data);
}
.menu__seatnext {
    color: var(--sos-display-ink-dim);
    font-family: var(--sos-font);
    font-weight: 400;
    font-size: 0.7em;
}
.menu__resseat {
    color: var(--sos-display-ink-dim);
    font-size: 1.3vw;
}

.menu__leaderboard {
    max-width: 60vw;
    width: 100%;
    margin: 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.menu__lbtop {
    flex: none;
}
.menu__lbmore {
    --menu-more-h: 24vh;
    flex: none;
    max-height: var(--menu-more-h);
    overflow: hidden;
    position: relative;
    margin-top: 0.5vh;
    -webkit-mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
    mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
}
// Hold at the top through the slide, then scroll down over the last 5s.
.menu__lbtrack.is-scrolling {
    animation-name: menu-lbscroll;
    animation-duration: 5s;
    animation-delay: calc(var(--menu-dwell, 22000ms) - 5000ms);
    animation-timing-function: ease-in-out;
    animation-fill-mode: both;
}
@keyframes menu-lbscroll {
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(calc(-100% + var(--menu-more-h)));
    }
}
.menu__lbrow {
    display: flex;
    align-items: center;
    gap: 1.6vw;
    padding: 1.1vh 0;
    border-bottom: 1px solid var(--sos-display-line);
    font-size: 1.9vw;
}
.menu__lbrow--lead {
    color: var(--sos-gold);
}
.menu__lbpos {
    font-family: var(--sos-font-head);
    font-weight: 700;
    min-width: 2.4ch;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--sos-display-ink-dim);
}
.menu__lbrow--lead .menu__lbpos {
    color: var(--sos-gold);
}
.menu__lbflag {
    width: 1.6vw;
    height: 1.6vw;
    border-radius: 50%;
    object-fit: cover;
    flex: none;
}
.menu__lbracer {
    flex: 1;
    min-width: 0;
    text-align: left;
}
.menu__lbname {
    display: block;
    font-family: var(--sos-font-head);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.menu__lbcar {
    display: block;
    font-family: var(--sos-font-mono);
    font-weight: 500;
    font-size: 1.1vw;
    line-height: 1.2;
    color: var(--sos-display-ink-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 0.2vh;
}
.menu__lbrow--lead .menu__lbcar {
    color: var(--sos-gold);
    opacity: 0.85;
}
.menu__lbtime {
    font-family: var(--sos-font-mono);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.menu__bottom {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    border-top: 1px solid var(--sos-display-line);
    padding-top: 2vh;
}
.menu__cta {
    display: flex;
    align-items: center;
    gap: 1.2vw;
    font-family: var(--sos-font-head);
    font-weight: 600;
    font-size: 1.1vw;
    color: var(--sos-display-ink-dim);
}
.menu__qr {
    width: 5vw;
    background: #fff;
    padding: 0.4vw;
    border-radius: 8px;
}
.menu__dots {
    position: absolute;
    right: 3vw;
    bottom: 2.5vh;
    display: flex;
    gap: 0.6vw;
    z-index: 3;
}
.menu__dot {
    cursor: pointer;
    width: 0.7vw;
    height: 0.7vw;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
}
.menu__dot.is-on {
    background: var(--sos-accent);
}

.menu-fade-enter-active,
.menu-fade-leave-active {
    transition: opacity 0.6s ease, transform 0.6s ease;
}
.menu-fade-enter {
    opacity: 0;
    transform: translateY(1.5vh);
}
.menu-fade-leave-to {
    opacity: 0;
    transform: translateY(-1.5vh);
}

.menu-carfade-enter-active,
.menu-carfade-leave-active {
    transition: opacity 2.4s ease;
}
.menu-carfade-enter,
.menu-carfade-leave-to {
    opacity: 0;
}

/* Loading screen: same dark ground as the menu, centred kick + label. */
.menu--loading {
    align-items: center;
    justify-content: center;
}
.menu__loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2.6vh;
}
.menu__loaderkick {
    width: 22vw;
    height: 0.9vw;
    background: linear-gradient(90deg, var(--sos-brand) 0 44%, transparent 44% 54%, var(--sos-teal) 54% 100%);
    transform: skewX(-18deg);
    animation: menu-loaderpulse 1.4s ease-in-out infinite;
}
@keyframes menu-loaderpulse {
    0%,
    100% {
        opacity: 0.35;
    }
    50% {
        opacity: 1;
    }
}
.menu__loadertext {
    font-family: var(--sos-font-head);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.34em;
    font-size: 1.15vw;
    color: var(--sos-display-ink-dim);
}
</style>
